// Deck entry point - Simplified for initial build

// Initialize constants early
import '../utils/constants.js';

// Import and initialize feature manager
import '../featureManager.js';

window.questus = window.questus || {};
window.questus.hideLoadingOverlay = function() {
  const overlay = document.getElementById('loadingOverlay');
  if (overlay) overlay.style.display = 'none';
  document.body.classList.remove('loading');
};

// Navigation function is now defined globally in base_vite.html
// No need to redefine it here

// Deck-specific initialization - no global HTMX listeners here!
// Global HTMX handlers are in base_vite.html where they belong

// Import and expose global functions
import { exposeQuestusCore } from '../exposeGlobals.js';

async function initDeckPage() {
  
  // Expose core functions to window.questus
  exposeQuestusCore();

  // Initialize breadcrumb navigation with event delegation
  const { initializeBreadcrumbNavigation, updateBreadcrumbNavigation } = await import('../utils/helpers/dom.js');
  initializeBreadcrumbNavigation();
  window.updateBreadcrumbNavigation = updateBreadcrumbNavigation;

  // Expose functions needed by edit pages
  const { loadDeck } = await import('../utils/state/deckState.js');
  const { getDistinctLanguageKeys, translateMissingLanguages, saveDeck } = await import('../utils/deck.js');
  const { initializeUILanguageDropdown } = await import('../utils/ui/languageUI.js');
  // Check for pending auth action (e.g., user selected Open Answer, was sent to Google OAuth, came back)
  const { resumePendingAuthAction } = await import('../utils/session.js');
  if (resumePendingAuthAction()) return; // Auto-started session, skip rest of init

  Object.assign(window, {
    loadDeck,
    getDistinctLanguageKeys,
    translateMissingLanguages,
    saveDeck,
    initializeUILanguageDropdown
  });
  
  try {
    // Folder tree initialization is now handled globally in global.js
    // No need to duplicate it here
    
    // Get the current path from URL params  
    const urlParams = new URLSearchParams(window.location.search);
    const currentPath = urlParams.get('path') || 'private';
    
    // Set the active tab based on the current path
    const setActiveTab = (path) => {
      // Remove active classes from all tabs and panes
      document.querySelectorAll('.nav-tabs .nav-link').forEach(tab => {
        tab.classList.remove('active');
        tab.setAttribute('aria-selected', 'false');
      });
      
      document.querySelectorAll('.tab-content .tab-pane').forEach(pane => {
        pane.classList.remove('active', 'show');
      });
      
      // Determine which tab should be active
      let activeTabId = 'private-tab';
      let activePaneId = 'private-tab-content';
      
      if (path.startsWith('public')) {
        activeTabId = 'public-tab';
        activePaneId = 'public-tab-content';
      } else if (path.startsWith('shared')) {
        activeTabId = 'shared-tab';
        activePaneId = 'shared-tab-content';
      }
      
      // Set the active tab
      const activeTab = document.getElementById(activeTabId);
      if (activeTab) {
        activeTab.classList.add('active');
        activeTab.setAttribute('aria-selected', 'true');
      }
      
      // Set the active pane
      const activePane = document.getElementById(activePaneId);
      if (activePane) {
        activePane.classList.add('active', 'show');
        // Force visibility and override Bootstrap fade behavior
        activePane.style.display = 'block';
        activePane.style.opacity = '1';
        activePane.style.visibility = 'visible';
        
      }
    };
    
    // Check which page we're on and initialize accordingly
    const isDecksListPage = window.location.pathname === '/deck/decks';
    const isDeckViewPage = window.location.pathname === '/deck/deck';
    
    if (isDecksListPage) {
      // Import and call pageDecksInitialization to set up event listeners and populate content
      try {
        const { populateDecksContainer, pageDecksInitialization } = await import('../pages/deck/decks.js');
        
        // Make pageDecksInitialization available globally for the template
        window.pageDecksInitialization = pageDecksInitialization;
        
        // Call the initialization function to set up event listeners
        pageDecksInitialization();
        
        // Set the correct active tab after content is loaded
        setActiveTab(currentPath);
        
      } catch (importError) {
        console.error('❌ Failed to import or call deck functions:', importError);
      }
    } else if (isDeckViewPage) {
      // For individual deck view page, import deck-specific functions
      try {
        const { pageDeckInitialization } = await import('../pages/deck/deck.js');
        
        // Make pageDeckInitialization available globally for the template
        window.pageDeckInitialization = pageDeckInitialization;
        
        // Don't call it immediately - let the template's pageInitialization handle it
        // This ensures consistent behavior between full page loads and HTMX swaps
      } catch (importError) {
        console.error('❌ Failed to import deck view functions:', importError);
      }
    }
    
    // Also import pageItemInitialization for the item page
    // Import it but don't call it - it will be called by the template's pageInitialization
    try {
      const { pageItemInitialization } = await import('../pages/deck/item.js');
      // Make it available globally for the item.html template
      window.pageItemInitialization = pageItemInitialization;
    } catch (error) {
      console.error('❌ Failed to import pageItemInitialization:', error);
    }
    
    window.questus.hideLoadingOverlay();
    
  } catch (error) {
    console.error('❌ Deck page initialization failed:', error);
    window.questus.hideLoadingOverlay();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initDeckPage);
} else {
  initDeckPage();
}